import { type CampaignFilters, EMPTY_CAMPAIGN_FILTERS, toCampaignFilters } from '@crm/shared';
import { Input, Label, Modal } from '@crm/ui';
import { useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import { useCampaignPreview } from '@/modules/campaigns/api';
import { AudienceFilters } from '@/modules/campaigns/components/audience-filters';
import { type ContactList, useCreateContactList, useUpdateContactList } from '../api';

interface ContactListModalProps {
  /** A lista sendo alterada - ausente, e uma lista nova. */
  list?: ContactList;
  /** Filtros de partida, quando a janela abre de dentro de uma campanha. */
  filters?: CampaignFilters;
  onClose: () => void;
  onSaved: (list: ContactList) => void;
}

/**
 * Criar ou alterar uma lista: um nome e o mesmo recorte de publico das
 * campanhas.
 *
 * A mesma janela serve a pagina de listas e o passo Publico - quem cria de
 * dentro de uma campanha nao aprende um segundo jeito de fazer a mesma coisa.
 */
export function ContactListModal({ list, filters, onClose, onSaved }: ContactListModalProps) {
  const criar = useCreateContactList();
  const alterar = useUpdateContactList();

  const [nome, setNome] = useState(list?.name ?? '');
  const [recorte, setRecorte] = useState<CampaignFilters>(
    toCampaignFilters(list?.filters ?? filters ?? EMPTY_CAMPAIGN_FILTERS),
  );

  // O tamanho aparece enquanto se escolhe: uma lista de zero pessoas so seria
  // descoberta na campanha, com a mensagem ja escrita.
  const preview = useCampaignPreview(recorte, 'COUPON', 1, 1);

  const salvando = criar.isPending || alterar.isPending;
  const erro = criar.error ?? alterar.error;

  async function salva() {
    if (!nome.trim()) return;

    const salva = list
      ? await alterar.mutateAsync({ id: list.id, input: { name: nome.trim(), filters: recorte } })
      : await criar.mutateAsync({ name: nome.trim(), filters: recorte });

    onSaved(salva);
  }

  return (
    <Modal
      open
      title={list ? 'Alterar lista' : 'Nova lista de contatos'}
      onClose={onClose}
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: 'Salvar',
          onClick: () => void salva(),
          disabled: !nome.trim() || salvando,
          loading: salvando,
        },
      ]}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="lista-nome">Nome da lista</Label>
          <Input
            id="lista-nome"
            placeholder="Clientes de Pinheiros"
            value={nome}
            onChange={(event) => setNome(event.target.value)}
          />
        </div>

        <AudienceFilters filters={recorte} onChange={setRecorte} />

        <p className="text-sm text-muted-foreground">
          {preview.isPending
            ? 'Contando...'
            : `${preview.data?.total ?? 0} contatos entram nesta lista hoje.`}
        </p>

        {erro ? <p className="text-sm text-destructive">{toErrorMessage(erro)}</p> : null}
      </div>
    </Modal>
  );
}
